import React, { useState, useEffect } from 'react';
import q2tlogo from '../../assets/img/q2t-logo.png';
import { useSearchParams } from "react-router-dom";
import { formatCardNumber, detectCardType, formatExpiry, isValidLuhn, validateField } from './cardForm.helpers';
import { BASE_URL } from '../../config/environments';
import { useCard } from '../../context/CardContext';

export default function CardForm({ handleSubmit, userId, mobileNumber, objectReferenceId, returnUrl }) {
  const { cardData } = useCard();
  const [params] = useSearchParams();
  const token = params.get('q');
  const creditCardContract = cardData;
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [cardType, setCardType] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');



  // Initializing formData after fields are available
  useEffect(() => {
    if (!creditCardContract || !creditCardContract.data?.form?.fields) return;

    const saved = sessionStorage.getItem('creditCardForm');
    if (saved) {
      setFormData(JSON.parse(saved));
    } else {
      const initial = {};
      creditCardContract.data.form.fields.forEach((field) => {
        initial[field.key] = field.type === 'checkbox' ? false : '';
      });
      setFormData(initial);
    }
  }, [creditCardContract]);

  if (!creditCardContract) {
    return <div className="text-center py-6">Loading form...</div>;
  }

  if (!creditCardContract || !formData) return null;

  const { title } = creditCardContract.meta;
  const fields = creditCardContract.data.form.fields;
  const actions = creditCardContract.data.form.actions;
  const infoBox = creditCardContract.data.infoBox;
  const footnote = creditCardContract.data.footnote;

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    if (name === 'cardNumber') {
      newValue = formatCardNumber(newValue);
      setCardType(detectCardType(newValue));
    }

    if (name === 'expiry') {
      newValue = formatExpiry(newValue);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // runnuing validation on fields on change
    const field = fields.find((f) => f.key === name);
    let error = validateField(field, newValue);

    if (name === 'expiry' && newValue.includes('/')) {
      const [mm, yy] = newValue.split('/');
      const inputDate = new Date(`20${yy}`, mm - 1);
      const now = new Date();
      now.setDate(1);
      if (inputDate < now) {
        error = 'Card expired';
      }
    }

    if (name === 'cardNumber') {
      const cleaned = newValue.replace(/\s/g, '');
      if (cleaned.length === 16 && !isValidLuhn(cleaned)) {
        error = 'Invalid card number';
      }
    }

    if (name === 'consent') {
      setFormData((prev) => ({ ...prev, consent: newValue }));
      return;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    } else {
      setErrors((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    }
  };



  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };


  const isValid = Object.keys(errors).length === 0;
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setTouched(Object.fromEntries(fields.map((f) => [f.key, true])));
    if (!isValid) return;

    const [expMonth, expYear] = formData.expiry.split('/');
    const payload = {
      cardNumber: formData.cardNumber.replace(/\s/g, ''),
      cardHolderName: formData.name,
      returnUrl,
      expiryDate: formData.expiry,
      objectReferenceId,
      cvv: formData.cvv,
      userId,
      mobileNumber: mobileNumber,
      paymentMethod: detectCardType(formData.cardNumber),
      cardExpMonth: expMonth,
      cardExpYear: expYear,
      consent: formData.consent.toString(),
      token
    };

    console.log("Submitting credit card payload:", payload);
    console.log("Request headers:", {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/v1/creditCard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      //no success message here
      handleSubmit?.(data);
    } catch (err) {
      setMessage('Error submitting the card. Please try again.');
      console.error("Submit error:", err);
    } finally {
      setLoading(false);
    }
  };
  const consentChecked = formData?.consent === true;
  const allFieldsValid = Object.keys(errors).length === 0;
  const allFieldsFilled = fields.every((field) => {
    if (field.required) {
      const value = formData[field.key];
      if (field.type === "checkbox") {
        return value;

      }
      return value?.toString().trim() !== "";
    }
    return true;
  });

  return (
    <div className="max-w-3xl mx-auto bg-white mt-0 sm:mt-4 p-8 rounded-none sm:rounded-3xl shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
        <img src={q2tlogo} alt="Quest2Travel Logo" className="w-36 h-12" />
      </div>

      {infoBox?.enabled && (
        <div className="bg-blue-50 text-sm text-gray-700 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-base font-bold mb-2"><strong>Important:</strong></p>
          <p className="text-gray-900">{infoBox.content}</p>
        </div>
      )}

      {message && <div className="mb-4 text-green-600 font-medium">{message}</div>}

      <form onSubmit={handleFormSubmit} className="space-y-4">
        {fields.map((field) => {
          const { key, label, placeholder} = field;
          const errorMsg = errors[key];
          const showError = touched[key] && errorMsg;

          if (key === 'expiry') {
            const cvvField = fields.find(f => f.key === 'cvv');
            return (
              <div key="expiry-cvv" className="flex gap-4">
                <div className="w-1/2">
                  <label htmlFor={key} className="text-sm block mb-2 font-semibold text-gray-900">{label}</label>
                  <input
                    id={key}
                    name={key}
                    placeholder={placeholder}
                    value={formData[key]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={5}
                    className={`w-full border px-4 py-2 h-12 rounded-lg ${showError ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {showError && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
                </div>

                <div className="w-1/2">
                  <label htmlFor={cvvField.key} className="text-sm block mb-2 font-semibold text-gray-900">{cvvField.label}</label>
                  <input
                    id={cvvField.key}
                    name={cvvField.key}
                    type="password"
                    placeholder={cvvField.placeholder}
                    value={formData[cvvField.key]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={3}
                    autoComplete='off'
                    className={`w-full border px-4 py-2 h-12 rounded-lg ${touched[cvvField.key] && errors[cvvField.key] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {touched[cvvField.key] && errors[cvvField.key] && (
                    <p className="text-red-500 text-sm mt-1">{errors[cvvField.key]}</p>
                  )}
                </div>
              </div>
            );
          }

          if (key === 'cvv') return null;

          if (key === 'consent') {
            return (
              <div key={key} className="pt-2">
                <div className="flex items-start gap-2 mt-2">
                  <input
                    type="checkbox"
                    id={key}
                    name={key}
                    checked={formData[key]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1"
                  />
                  <p className="text-sm text-gray-700 leading-snug">
                    {label}
                  </p>
                </div>
              </div>
            );
          }


          return (
            <div key={key}>
              <label htmlFor={key} className="text-sm block mb-2 font-semibold text-gray-900">{label}</label>
              <div className="relative">
                <input
                  id={key}
                  name={key}
                  placeholder={placeholder}
                  type="text"
                  value={formData[key]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={key === 'cardNumber' ? 19 : undefined}
                  className={`w-full border px-4 py-2 h-12 ${key === 'cardNumber' ? 'pr-12' : ''} rounded-lg ${showError ? 'border-red-500' : 'border-gray-300'}`}
                />
                {key === 'cardNumber' && cardType && (
                  <img
                    src={`https://img.icons8.com/color/48/000000/${cardType}.png`}
                    alt={cardType}
                    className="absolute top-1/2 right-3 -translate-y-1/2 h-6"
                  />
                )}
              </div>
              {showError && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
            </div>
          );

        })}

        <div className="flex gap-4 pt-6">
          {actions.map((action) => {
            const isDisabled =
              action.disabledIfInvalid &&
              (!allFieldsValid || !allFieldsFilled || !consentChecked || loading);

            return (
              <button
                key={action.name}
                type={action.type === "submit" ? "submit" : "button"}
                disabled={isDisabled}
                onClick={async () => {
                  if (action.name === "cancel") {
                    const confirmCancel = window.confirm(
                      "Are you sure you want to cancel?"
                    );
                    if (!confirmCancel) return;

                    try {
                      const res = await fetch(`${BASE_URL}/api/v1/cancelCard`, {
                        method: "GET",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                      });

                      if (!res.ok) throw new Error("Cancel request failed");

                      const data = await res.json();
                      if (data?.returnUrl) {
                        window.location.href = data.returnUrl;
                      }
                    } catch (err) {
                      console.error(err);
                      alert("Failed to cancel card. Please try again.");
                    }
                  }
                }}
                className={`w-full py-2 rounded-lg font-semibold h-12 transition ${action.style === "primary"
                  ? isDisabled
                    ? "bg-blue-200 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                  : "border border-gray-300 hover:bg-gray-100"
                  }`}
              >
                {loading && action.type === "submit" ? "Submitting..." : action.label}
              </button>
            );
          })}
        </div>

      </form>

      {footnote?.enabled && (
        <div className="mt-6 text-sm text-gray-600 space-y-2 p-4 bg-gray-100 rounded-lg">
          {footnote.content.map((item, idx) => (
            <p className="text-gray-500" key={idx}>
              <strong>{item.title}:</strong> {item.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
